<template>
  <div class="qr-table-container">
    <a-table
      :columns="columns"
      :data-source="items"
      :loading="isLoading"
      :pagination="false"
      :row-key="record => record._id"
      :scroll="{ x: 1300 }"
    >
      <!-- QR 미리보기 -->
      <template #qrPreview="{ record }">
        <div v-if="getQrContentUrl(record)" class="qr-preview-cell" @click="showQrPreview(record)">
          <div class="qr-thumbnail" ref="qrContainer" :data-id="record._id">
            <canvas :id="`qr-${record._id}`" class="qr-canvas" width="50" height="50"></canvas>
          </div>
        </div>
        <div v-else class="qr-unavailable" role="img" aria-label="QR코드를 표시할 수 없습니다">
          <span aria-hidden="true">—</span>
        </div>
      </template>

      <!-- 이름/설명 -->
      <template #nameInfo="{ record }">
        <div class="name-info">
          <div v-if="isOwnedQr(record)" class="owner-info">
            <a-tag color="purple">{{ ownedQrMeta(record).label }}</a-tag>
          </div>
          <div class="qr-name">{{ record.name }}</div>
          <div class="qr-desc text-muted text-small" v-if="record.description">
            {{ record.description }}
          </div>
          <div v-if="record.trackingEnabled === false" class="qr-link-info">
            <span class="direct-link-label">직접 연결</span>
            <span class="qr-link-hint">QR에 단축 URL 미사용</span>
          </div>
          <div v-else-if="getShortQrUrl(record)" class="qr-link-info">
            <a-tooltip :title="getShortQrUrl(record)" :trigger="['hover', 'focus']">
              <code class="qr-short-url" tabindex="0" :aria-label="getShortQrUrl(record)">
                <span class="short-url-base">{{ getShortUrlBase(record) }}</span>
                <span class="short-url-path">/qr/{{ record.shortCode }}</span>
              </code>
            </a-tooltip>
            <a-button
              size="small"
              class="short-url-copy"
              data-action="copy-short-url"
              aria-label="단축 URL 복사"
              @click.stop="copyUrl(getShortQrUrl(record), '단축 URL')"
            >
              <template #icon><copy-outlined /></template>
              단축 URL 복사
            </a-button>
          </div>
          <div v-else class="qr-link-info">
            <span class="qr-link-hint">단축 URL을 확인할 수 없습니다</span>
          </div>
        </div>
      </template>

      <!-- 카테고리 -->
      <template #category="{ record }">
        <span v-if="record.categoryId">{{ record.categoryId.name }}</span>
        <span v-else class="text-muted">-</span>
      </template>

      <!-- 태그 -->
      <template #tags="{ record }">
        <div class="tags-cell" v-if="getValidTags(record.tagIds).length > 0">
          <span
            v-for="tag in getValidTags(record.tagIds).slice(0, 3)"
            :key="tag._id"
            class="tag-badge"
            :style="{ backgroundColor: (tag.color || '#007bff') + '20', color: tag.color || '#007bff' }"
          >
            {{ tag.name }}
          </span>
          <span v-if="getValidTags(record.tagIds).length > 3" class="tag-more">
            +{{ getValidTags(record.tagIds).length - 3 }}
          </span>
        </div>
        <span v-else class="text-muted">-</span>
      </template>

      <!-- 목적지 URL -->
      <template #targetUrl="{ record }">
        <div class="url-cell">
          <a :href="record.targetUrl" target="_blank" rel="noopener noreferrer">
            {{ record.targetUrl }}
          </a>
          <a-tooltip title="목적지 URL 복사">
            <a-button
              type="link"
              size="small"
              class="target-url-copy"
              aria-label="목적지 URL 복사"
              :disabled="!record.targetUrl"
              @click.stop="copyUrl(record.targetUrl, '목적지 URL')"
            >
              <template #icon><copy-outlined /></template>
            </a-button>
          </a-tooltip>
        </div>
      </template>

      <!-- 스캔 횟수 -->
      <template #scanCount="{ record }">
        <template v-if="record.trackingEnabled !== false">
          <span class="scan-count">{{ addCommaToNumber(record.scanCount || 0) }}</span>
        </template>
        <template v-else>
          <a-tooltip title="통계 수집이 비활성화된 QR코드입니다">
            <span class="scan-count disabled">-</span>
          </a-tooltip>
        </template>
      </template>

      <!-- 통계 수집 -->
      <template #tracking="{ record }">
        <a-tag v-if="record.trackingEnabled !== false" color="blue">
          <i class="fa-solid fa-chart-line" style="margin-right: 4px;"></i>수집
        </a-tag>
        <a-tooltip v-else title="QR코드가 목적지 URL로 직접 연결됩니다. URL 변경이 불가능합니다.">
          <a-tag color="default">
            <i class="fa-solid fa-link" style="margin-right: 4px;"></i>직접URL
          </a-tag>
        </a-tooltip>
      </template>

      <!-- 상태 -->
      <template #status="{ record }">
        <a-tooltip :title="isOwnedQr(record) ? ownedQrMeta(record).tooltip : ''">
          <a-switch
            :checked="record.status === 'active'"
            :disabled="isOwnedQr(record)"
            checked-children="활성"
            un-checked-children="비활성"
            @change="(checked) => handleStatusChange(record, checked)"
          />
        </a-tooltip>
      </template>

      <!-- 액션 -->
      <template #action="{ record }">
        <div class="action-buttons">
          <a-tooltip :title="isOwnedQr(record) ? '상세' : '상세/수정'">
            <a-button type="link" size="small" @click="handleEdit(record)">
              <template #icon><edit-outlined /></template>
            </a-button>
          </a-tooltip>
          <a-tooltip
            :title="isOwnedQr(record)
              ? ownedQrMeta(record).tooltip
              : (record.trackingEnabled !== false ? 'URL 변경' : '직접 URL QR코드는 URL 변경 불가')"
          >
            <a-button
              type="link"
              size="small"
              :disabled="record.trackingEnabled === false || isOwnedQr(record)"
              @click="handleUrlChange(record)"
            >
              <template #icon><link-outlined /></template>
            </a-button>
          </a-tooltip>
          <a-tooltip :title="record.trackingEnabled !== false ? 'QR 스캔 통계 열기' : '직접 URL QR코드는 통계 미제공'">
            <a-button
              type="link"
              size="small"
              data-action="open-stats"
              :data-qr="record.shortCode"
              :disabled="record.trackingEnabled === false"
              @click="handleStats(record)"
            >
              <template #icon><bar-chart-outlined /></template>
            </a-button>
          </a-tooltip>
          <a-tooltip title="다운로드">
            <a-button type="link" size="small" @click="handleDownload(record)">
              <template #icon><download-outlined /></template>
            </a-button>
          </a-tooltip>
          <a-tooltip :title="isOwnedQr(record) ? ownedQrMeta(record).tooltip : '삭제'">
            <a-button
              type="link"
              size="small"
              danger
              :disabled="isOwnedQr(record)"
              @click="handleDelete(record)"
            >
              <template #icon><delete-outlined /></template>
            </a-button>
          </a-tooltip>
        </div>
      </template>
    </a-table>
  </div>
</template>

<script>
import { defineComponent, onMounted, watch, nextTick } from 'vue';
import {
  EditOutlined,
  DeleteOutlined,
  LinkOutlined,
  BarChartOutlined,
  DownloadOutlined,
  CopyOutlined
} from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';
import { useRouter } from 'vue-router';
import { addCommaToNumber } from '@/utils/utils';
import {
  resolveShortQrUrl,
  resolveQrContentUrl,
  isOwnedQr,
  ownedQrMeta
} from '../utils/qrUrls';
import QRCode from 'qrcode';

export default defineComponent({
  name: 'QrTable',
  components: {
    EditOutlined,
    DeleteOutlined,
    LinkOutlined,
    BarChartOutlined,
    DownloadOutlined,
    CopyOutlined
  },
  props: {
    items: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    baseUrl: {
      type: String,
      default: ''
    },
    tags: {
      type: Array,
      default: () => []
    }
  },
  emits: ['edit', 'delete', 'status-change', 'url-change', 'download', 'preview'],
  setup(props, { emit }) {
    const router = useRouter();
    const columns = [
      {
        title: 'QR',
        key: 'qrPreview',
        width: 80,
        align: 'center',
        slots: { customRender: 'qrPreview' }
      },
      {
        title: 'QR코드 정보',
        key: 'nameInfo',
        width: 250,
        slots: { customRender: 'nameInfo' }
      },
      {
        title: '카테고리',
        key: 'category',
        width: 120,
        slots: { customRender: 'category' }
      },
      {
        title: '태그',
        key: 'tags',
        width: 180,
        slots: { customRender: 'tags' }
      },
      {
        title: '목적지 URL',
        key: 'targetUrl',
        width: 300,
        slots: { customRender: 'targetUrl' }
      },
      {
        title: '통계',
        key: 'tracking',
        width: 90,
        align: 'center',
        slots: { customRender: 'tracking' }
      },
      {
        title: '스캔',
        key: 'scanCount',
        width: 70,
        align: 'center',
        slots: { customRender: 'scanCount' }
      },
      {
        title: '상태',
        key: 'status',
        width: 100,
        align: 'center',
        slots: { customRender: 'status' }
      },
      {
        title: '관리',
        key: 'action',
        width: 180,
        align: 'center',
        fixed: 'right',
        slots: { customRender: 'action' }
      }
    ];

    const getShortQrUrl = (record) => resolveShortQrUrl(props.baseUrl || window.location.origin, record);
    const getQrContentUrl = (record) => resolveQrContentUrl(props.baseUrl || window.location.origin, record);

    const getShortUrlBase = (record) => {
      const shortUrl = getShortQrUrl(record);
      const path = `/qr/${record.shortCode}`;
      return shortUrl.slice(0, -path.length).replace(/^https?:\/\//i, '');
    };

    const generateQrCodes = async () => {
      await nextTick();
      for (const item of props.items) {
        const canvas = document.getElementById(`qr-${item._id}`);
        if (canvas) {
          const qrUrl = getQrContentUrl(item);
          if (!qrUrl) {
            canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
            continue;
          }
          try {
            await QRCode.toCanvas(canvas, qrUrl, {
              width: 50,
              margin: 1,
              color: {
                dark: '#000000',
                light: '#ffffff'
              }
            });
          } catch (err) {
            console.error('QR 생성 오류:', err);
          }
        }
      }
    };

    onMounted(() => {
      generateQrCodes();
    });

    watch([() => props.items, () => props.baseUrl], () => {
      generateQrCodes();
    }, { deep: true });

    const showQrPreview = (record) => {
      if (getQrContentUrl(record)) {
        emit('preview', record);
      }
    };

    const handleEdit = (record) => {
      emit('edit', record);
    };

    const handleDelete = (record) => {
      emit('delete', record);
    };

    const handleStatusChange = (record, checked) => {
      emit('status-change', record, checked ? 'active' : 'inactive');
    };

    const handleUrlChange = (record) => {
      emit('url-change', record);
    };

    // 통계: /qr/stats 탐색 페이지로 이동 (해당 QR 선택 상태)
    const handleStats = (record) => {
      router.push({ name: 'qrStats', query: { qr: record._id } });
    };

    const handleDownload = (record) => {
      emit('download', record);
    };

    const copyUrl = async (url, label) => {
      if (!url) {
        message.error(`${label}을 확인할 수 없습니다.`);
        return;
      }
      try {
        if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
          message.error(`${label} 복사에 실패했습니다. 브라우저의 클립보드 접근을 확인해 주세요.`);
          return;
        }
        await navigator.clipboard.writeText(url);
        message.success(`${label}이 복사되었습니다.`);
      } catch {
        message.error(`${label} 복사에 실패했습니다. 브라우저의 클립보드 접근을 확인해 주세요.`);
      }
    };

    // 태그 ID(문자열 또는 객체)를 태그 객체로 변환
    const getValidTags = (tagIds) => {
      if (!tagIds || !Array.isArray(tagIds)) return [];

      return tagIds
        .map(tag => {
          // 이미 객체인 경우 (populate된 경우)
          if (tag && typeof tag === 'object' && tag.name) {
            return tag;
          }
          // 문자열인 경우 (populate 안 된 경우) - props.tags에서 찾기
          if (typeof tag === 'string') {
            return props.tags.find(t => t._id === tag);
          }
          return null;
        })
        .filter(tag => tag && tag.name); // null 및 name 없는 항목 제거
    };

    return {
      columns,
      addCommaToNumber,
      showQrPreview,
      handleEdit,
      handleDelete,
      handleStatusChange,
      handleUrlChange,
      handleStats,
      handleDownload,
      getShortQrUrl,
      getQrContentUrl,
      getShortUrlBase,
      copyUrl,
      getValidTags,
      isOwnedQr,
      ownedQrMeta
    };
  }
});
</script>

<style lang="scss" scoped>
@import '../styles/variables.scss';

.qr-table-container {
  background-color: $bg-white;
  border-radius: $border-radius-md;
  overflow: hidden;
}

.qr-preview-cell {
  cursor: pointer;

  .qr-thumbnail {
    display: flex;
    align-items: center;
    justify-content: center;

    .qr-canvas {
      border: 1px solid $border-color;
      border-radius: $border-radius-sm;
    }
  }
}

.qr-unavailable {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  box-sizing: border-box;
  margin: 0 auto;
  border: 1px solid $border-color;
  border-radius: $border-radius-sm;
  background-color: $bg-light;
  color: $text-muted;
}

.name-info {
  min-width: 0;
  max-width: 218px;

  .owner-info {
    margin-bottom: 2px;
  }

  .qr-name {
    font-weight: $font-weight-medium;
    color: $text-dark;
    margin-bottom: 2px;
  }

  .qr-desc {
    margin-bottom: 4px;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .qr-link-info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    min-width: 0;
    max-width: 100%;
    margin-top: 6px;
  }

  .qr-short-url {
    display: flex;
    align-items: baseline;
    max-width: 100%;
    min-width: 0;
    color: $text-muted;
    font-size: $font-size-xs;
    line-height: 20px;
  }

  .short-url-base {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .short-url-path {
    flex-shrink: 0;
    color: $text-dark;
    white-space: nowrap;
  }

  .short-url-copy {
    flex-shrink: 0;
    height: 28px;
    padding: 0 8px;
    font-size: $font-size-xs;
    white-space: nowrap;
  }

  .direct-link-label {
    font-size: $font-size-xs;
    font-weight: $font-weight-medium;
    color: $text-muted;
  }

  .qr-link-hint {
    color: $text-muted;
    font-size: $font-size-xs;
    line-height: 20px;
  }
}

.tags-cell {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;

  .tag-badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: $font-size-xs;
    font-weight: $font-weight-medium;
  }

  .tag-more {
    font-size: $font-size-xs;
    color: $text-muted;
  }
}

.url-cell {
  display: flex;
  align-items: center;
  max-width: 280px;

  a {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: $primary-color;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  .target-url-copy {
    flex-shrink: 0;
  }
}

.scan-count {
  font-weight: $font-weight-bold;
  color: $primary-color;

  &.disabled {
    color: $text-muted;
    font-weight: normal;
  }
}

.action-buttons {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.text-muted {
  color: $text-muted;
}

.text-small {
  font-size: $font-size-sm;
}
</style>
